import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { MessageCircle, Calendar, Sparkles } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { getPageWaLink } from "../data/firm";
import { resolveWaKey } from "../lib/waKey";

/**
 * Floating sticky WhatsApp button — bottom-right (LTR) / bottom-left
 * (RTL) on every page. Includes a discreet permanent label ("تحتاج
 * مساعدة؟") and a hover-revealed long label ("تواصل معنا عبر واتساب").
 */
export const StickyWhatsApp = () => {
  const { lang } = useI18n();
  const { pathname } = useLocation();
  const isAr = lang === "ar";

  const waLink = useMemo(
    () => getPageWaLink(resolveWaKey(pathname), lang),
    [pathname, lang]
  );

  const shortLabel = isAr ? "تحتاج مساعدة؟" : "Need help?";
  const longLabel = isAr ? "تواصل معنا عبر واتساب" : "Chat with us on WhatsApp";

  // Hide on /admin and /portal — those workspaces have their own chrome.
  if (pathname.startsWith("/admin") || pathname.startsWith("/portal")) {
    return null;
  }

  return (
    <div
      data-testid="sticky-whatsapp-wrap"
      className="fixed bottom-24 lg:bottom-8 end-5 z-40 flex items-center gap-2 group"
    >
      {/* Persistent small chip (desktop only) */}
      <span
        className="hidden lg:flex items-center px-3 h-9 rounded-full text-xs font-medium shadow-sm transition-all duration-300 group-hover:opacity-0 group-hover:translate-x-2 rtl:group-hover:-translate-x-2"
        style={{ background: "var(--bg)", color: "var(--ink)", border: "1px solid var(--line)" }}
      >
        {shortLabel}
      </span>
      {/* Hover-revealed long label */}
      <span
        className="hidden lg:flex absolute end-16 items-center px-3 h-9 rounded-full text-xs font-medium shadow-md opacity-0 -translate-x-2 rtl:translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 pointer-events-none whitespace-nowrap"
        style={{ background: "var(--navy)", color: "#fff" }}
      >
        {longLabel}
      </span>

      <a
        href={waLink}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={longLabel}
        data-testid="sticky-whatsapp"
        className="relative"
      >
        <span className="absolute inset-0 rounded-full animate-ping" style={{ background: "var(--wa-soft)", opacity: 0.35 }} />
        <span
          className="relative flex items-center justify-center w-12 h-12 lg:w-14 lg:h-14 rounded-full text-white shadow-lg transition-transform duration-300 group-hover:scale-110"
          style={{ background: "var(--wa)", boxShadow: "0 10px 24px -8px rgba(8,55,40,0.55)" }}
        >
          <MessageCircle className="w-5 h-5 lg:w-6 lg:h-6" />
        </span>
      </a>
    </div>
  );
};

/**
 * Mobile-only bottom bar with the 3 primary CTAs that should be one
 * thumb-tap away on any page.
 */
export const MobileBottomBar = () => {
  const { lang } = useI18n();
  const { pathname } = useLocation();
  const isAr = lang === "ar";

  if (pathname.startsWith("/admin") || pathname.startsWith("/portal")) {
    return null;
  }

  const waLink = getPageWaLink(resolveWaKey(pathname), lang);

  const items = [
    {
      icon: Calendar,
      label: isAr ? "احجز" : "Book",
      href: waLink,
      external: true,
      testid: "bottom-bar-book",
    },
    {
      icon: MessageCircle,
      label: isAr ? "واتساب" : "WhatsApp",
      href: waLink,
      external: true,
      testid: "bottom-bar-whatsapp",
      accent: true,
    },
    {
      icon: Sparkles,
      label: isAr ? "ذكاء قانوني" : "AI",
      to: "/ai-legal-assistant",
      testid: "bottom-bar-ai",
    },
  ];

  return (
    <nav
      data-testid="mobile-bottom-bar"
      className="lg:hidden fixed inset-x-0 bottom-0 z-40 border-t backdrop-blur"
      style={{
        background: "rgba(252,250,246,0.92)",
        borderColor: "var(--line)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      <ul className="grid grid-cols-3">
        {items.map((it) => {
          const Inner = (
            <span
              className="flex flex-col items-center justify-center gap-1 py-2.5 text-[11px] font-medium active:bg-[var(--beige)] transition-colors"
              style={{ color: "var(--ink)" }}
            >
              <it.icon
                className="w-5 h-5"
                style={{ color: it.accent ? "var(--wa)" : "var(--gold-deep)" }}
              />
              {it.label}
            </span>
          );
          return (
            <li key={it.label} className="border-s first:border-s-0" style={{ borderColor: "var(--line)" }}>
              {it.external ? (
                <a href={it.href} target="_blank" rel="noopener noreferrer"
                   data-testid={it.testid} className="block">{Inner}</a>
              ) : (
                <Link to={it.to} data-testid={it.testid} className="block">{Inner}</Link>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};
